import texts from '../texts.json'
import * as types from './types'

function isNotEmptyString(value) {
  return value !== ''
}

function isBetweenRange(value, range = [0, 100]) {
  const [min, max] = range

  return isNotEmptyString(value) && value >= min && value <= max
}

function getSelectedOption(options, selectedValue) {
  const pos = options.findIndex((option) => {
    return option.value === selectedValue
  })

  return pos !== -1 ? options[pos].text : ''
}

const numberPattern = /^\d+$/

export default {
  [types.FORM_RULES]: function (state) {
    const { validationLimits } = state

    // PRICING RULES
    const pricingRules = [
      (value) => {
        return isBetweenRange(value) || 'Must be a number between 0 and 100'
      }
    ]

    // BILLING RULES
    const billingCycleDurationRules = (cycleIsMonth) => {
      if (!cycleIsMonth) {
        return [
          (value) => {
            return (
              isBetweenRange(
                value,
                validationLimits.billingCycleDurationDays
              ) || 'Must be a number between 1 and 90 days'
            )
          }
        ]
      } else {
        return [
          (value) => {
            return (
              isBetweenRange(
                value,
                validationLimits.billingCycleDurationMonths
              ) || 'Must be a number between 1 and 3 months'
            )
          }
        ]
      }
    }

    const billingCycleStartDayRules = [
      (value) => {
        return (
          isBetweenRange(value, validationLimits.billingCycleStartDayMonths) ||
          'Must be a number between 1 and 28'
        )
      }
    ]

    // PAYMENT TERM RULES
    const paymentTermDurationRules = [
      (value) => {
        return (
          isBetweenRange(value, validationLimits.paymentTermDuration) ||
          'Must be a number between 1 and 28'
        )
      }
    ]

    // REPAYMENT RULES
    const minRepaymentFlat = [
      (value) => {
        return value >= 0 || 'Must be a number greater than or equal to 0'
      },
      (value) => {
        return numberPattern.test(value) || 'Must be a number'
      }
    ]

    const minRepaymentPercentage = [
      (value) => {
        return isBetweenRange(value) || 'Must be a number between 0 and 100'
      }
    ]

    return {
      pricing: pricingRules,
      billing: {
        cycleDuration: (unit) => billingCycleDurationRules(unit),
        cycleStartDay: billingCycleStartDayRules
      },
      paymentTermDuration: paymentTermDurationRules,
      repayment: {
        minRepaymentFlat,
        minRepaymentPercentage
      }
    }
  },
  [types.SELECTED_PAYMENT_TERM_UNIT]: function (state) {
    return getSelectedOption(
      state.paymentTermUnitOptions,
      state.pricing.paymentTermUnit
    )
  },
  [types.SELECTED_REPAYMENT_METHOD]: function (state) {
    return getSelectedOption(
      state.repaymentMethodOptions,
      state.pricing.repaymentMethod
    )
  },
  [types.SELECTED_REPAYMENT_INTEREST_METHOD]: function (state) {
    return getSelectedOption(
      state.repaymentInterestMethodOptions,
      state.pricing.repaymentInterestMethod
    )
  },
  [types.SELECTED_BILLING_UNIT]: function (state) {
    return getSelectedOption(
      state.billingUnitOptions,
      state.pricing.billingCycleUnit
    )
  },
  [types.IS_FORM_VALID]: function (state) {
    const { validationLimits } = state
    const {
      pricingAnnualOverdueInterestRate,
      billingCycleUnit,
      billingCycleDuration,
      billingCycleStartDay,
      paymentTermUnit,
      paymentTermDuration,
      repaymentMethod,
      repaymentMinimumFlatAmount,
      repaymentInterestMethod,
      repaymentMinimumPercentage,
      pricingAnnualWithdrawalInterestRate,
      pricingAnnualInterestRate
    } = state.pricingToBeSaved

    const isValid = {
      pricingAnnualOverdueInterestRate: isBetweenRange(
        pricingAnnualOverdueInterestRate
      ),
      pricingAnnualWithdrawalInterestRate: isBetweenRange(
        pricingAnnualWithdrawalInterestRate
      ),
      pricingAnnualInteresRate: isBetweenRange(pricingAnnualInterestRate),
      billingCycleUnit: isNotEmptyString(billingCycleUnit),
      billingCycleDuration:
        billingCycleUnit === texts.unitTextsValue.month
          ? isBetweenRange(
              billingCycleDuration,
              validationLimits.billingCycleDurationMonths
            )
          : isBetweenRange(
              billingCycleDuration,
              validationLimits.billingCycleDurationDays
            ),
      billingCycleStartDay:
        billingCycleUnit === texts.unitTextsValue.month
          ? isBetweenRange(
              billingCycleStartDay,
              validationLimits.billingCycleStartDayMonths
            )
          : true,
      paymentTermUnit: isNotEmptyString(paymentTermUnit),
      paymentTermDuration: isBetweenRange(
        paymentTermDuration,
        validationLimits.paymentTermDuration
      ),
      repaymentMethod: isNotEmptyString(repaymentMethod),
      repaymentMinimumFlatAmount:
        isNotEmptyString(repaymentMinimumFlatAmount) &&
        repaymentMinimumFlatAmount >= 0,
      repaymentInterestMethod: isNotEmptyString(repaymentInterestMethod),
      repaymentMinimumPercentage: isBetweenRange(repaymentMinimumPercentage)
    }

    return Object.values(isValid).every((value) => value === true)
  },
  [types.FORMAT_VALUES_TO_SEND]: function (state) {
    const pricing = state.pricingToBeSaved
    return {
      ...pricing,
      ['pricingAnnualWithdrawalInterestRate']:
        pricing.pricingAnnualWithdrawalInterestRate / 100,
      ['pricingAnnualOverdueInterestRate']:
        pricing.pricingAnnualOverdueInterestRate / 100,
      ['pricingAnnualInterestRate']: pricing.pricingAnnualInterestRate / 100,
      ['repaymentMinimumFlatAmount']: toCents(
        pricing.repaymentMinimumFlatAmount
      )
    }
  }
}
